import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

type Party = {
  firstName?: string;
  lastName?: string;
  title?: string;
  company?: string;
  id?: string;
};

type MeetingSummary = Record<string, string[]>;

type Recording = {
  id?: string;
  status?: string;
  selectedTimeSlot?: { from?: string; to?: string };
  thumbnailUrl?: string;
  meetingSummary?: MeetingSummary | null;
  zoomRecordingDetails?: {
    recordings?: string;
    downloadUrls?: Record<string, string>;
  };
  ambassador?: Party;
  client?: Party;
};

export type ConsolidatedStatementViewerRole = "ambassador" | "vendor";

export interface PdfScopeBlock {
  primary: string;
  secondary?: string;
  tertiary?: string;
}

function fullName(p?: Party) {
  return [p?.firstName, p?.lastName].filter(Boolean).join(" ") || "-";
}

function formatDateTimeRange(slot?: { from?: string; to?: string }) {
  if (!slot?.from || !slot?.to) return { date: "-", time: "-" };
  const from = dayjs(slot.from);
  const to = dayjs(slot.to);
  return {
    date: from.format("ddd, MMM D, YYYY"),
    time: `${from.format("h:mm A")} - ${to.format("h:mm A")}`,
  };
}

export function buildConsolidatedStatementPdfForList(params: {
  recordings: Recording[];
  viewerRole: ConsolidatedStatementViewerRole;
  pdfScope?: PdfScopeBlock | null;
}) {
  const { recordings, viewerRole, pdfScope } = params;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const createdOn = dayjs().format("MMM D, YYYY");

  const NAVY: [number, number, number] = [10, 48, 92];
  const TABLE_BORDER: [number, number, number] = [200, 203, 208];
  const TEXT_DARK: [number, number, number] = [20, 23, 28];

  const accentBarWidth = 2.25;
  const scopeBarPadV = 5;
  const scopeToGeneratedGap = 11;
  const ascentPt = (fontSize: number) => fontSize * 0.72;
  const descentPt = (fontSize: number) => fontSize * 0.28;

  const drawTitleBlock = (): number => {
    const headerY = 52;
    const colGap = 16;
    const usableW = pageWidth - 2 * marginX;
    const titleText = "Consolidated Summary";
    const stmtLineH = 16;

    const scope = pdfScope?.primary?.trim()
      ? pdfScope
      : null;

    let y: number;

    if (!scope) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(...TEXT_DARK);
      const statementLines = doc.splitTextToSize(titleText, usableW) as string[];
      doc.text(statementLines, pageWidth - marginX, headerY, {
        align: "right",
        maxWidth: usableW,
      });
      y =
        headerY +
        Math.max(statementLines.length * stmtLineH, stmtLineH) +
        14;
    } else {
      const stmtColW = Math.min(210, (usableW - colGap) * 0.4);
      const scopeColW = usableW - colGap - stmtColW;
      const scopeTextInsetCol = 8;
      const scopeTextMaxCol = Math.max(
        100,
        scopeColW - accentBarWidth - scopeTextInsetCol
      );
      const scopeTextXCol = marginX + accentBarWidth + scopeTextInsetCol;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12.5);
      const primaryLines = doc.splitTextToSize(
        scope.primary.trim(),
        scopeTextMaxCol
      ) as string[];

      let secLines: string[] = [];
      if (scope.secondary?.trim()) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        secLines = doc.splitTextToSize(
          scope.secondary.trim(),
          scopeTextMaxCol
        ) as string[];
      }

      let terLines: string[] = [];
      if (scope.tertiary?.trim()) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8.75);
        terLines = doc.splitTextToSize(
          scope.tertiary.trim(),
          scopeTextMaxCol
        ) as string[];
      }

      const primaryLineH = 14;
      const secondaryLineH = 11;
      const tertiaryLineH = 10;

      let cy = headerY;
      const firstBaseline = cy;
      let lastBaseline =
        firstBaseline + Math.max(0, primaryLines.length - 1) * primaryLineH;
      let lastFontSize = 12.5;
      cy += primaryLines.length * primaryLineH;

      if (secLines.length > 0) {
        const secFirst = cy + 2;
        lastBaseline =
          secFirst + Math.max(0, secLines.length - 1) * secondaryLineH;
        lastFontSize = 9.5;
        cy = secFirst + secLines.length * secondaryLineH + 4;
      }

      if (terLines.length > 0) {
        const terFirst = cy;
        lastBaseline =
          terFirst + Math.max(0, terLines.length - 1) * tertiaryLineH;
        lastFontSize = 8.75;
        cy = terFirst + terLines.length * tertiaryLineH + 2;
      }

      const visualTop = firstBaseline - ascentPt(12.5);
      const visualBottom = lastBaseline + descentPt(lastFontSize);
      const barTop = visualTop - scopeBarPadV;
      const barBottom = visualBottom + scopeBarPadV;

      doc.setFillColor(...NAVY);
      doc.rect(marginX, barTop, accentBarWidth, barBottom - barTop, "F");

      cy = headerY;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12.5);
      doc.setTextColor(...TEXT_DARK);
      doc.text(primaryLines, scopeTextXCol, cy);
      cy += primaryLines.length * primaryLineH;

      if (secLines.length > 0) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(52, 58, 66);
        doc.text(secLines, scopeTextXCol, cy + 2);
        cy += secLines.length * secondaryLineH + 4;
      }

      if (terLines.length > 0) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8.75);
        doc.setTextColor(95, 102, 112);
        doc.text(terLines, scopeTextXCol, cy);
        cy += terLines.length * tertiaryLineH + 2;
      }

      const scopeBandBottom = Math.max(barBottom, cy) + 2;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(...TEXT_DARK);
      const statementLines = doc.splitTextToSize(titleText, stmtColW) as string[];
      doc.text(statementLines, pageWidth - marginX, headerY, {
        align: "right",
        maxWidth: stmtColW,
      });

      const stmtBandBottom = headerY + statementLines.length * stmtLineH;

      y = Math.max(scopeBandBottom, stmtBandBottom) + scopeToGeneratedGap;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(70, 75, 82);
    doc.text(`Generated on ${createdOn}`, marginX, y);
    doc.text(`Meetings: ${recordings.length}`, pageWidth - marginX, y, {
      align: "right",
    });
    y += 14;

    doc.setDrawColor(...TABLE_BORDER);
    doc.setLineWidth(0.75);
    doc.line(marginX, y, pageWidth - marginX, y);
    return y;
  };

  const getDisplayParty = (r: Recording) =>
    viewerRole === "ambassador" ? r?.client : r?.ambassador;

  const getSummaryBuckets = (r: Recording) => {
    const s = r?.meetingSummary || undefined;
    const recap = s?.["Meeting Recap"] || s?.["MEETING RECAP"] || [];
    const next = s?.["Next Steps"] || s?.["NEXT STEPS"] || [];
    return { recap, next };
  };

  const asBullets = (items: string[], maxWidth: number) => {
    const safe = Array.isArray(items) ? items : [];
    const lines: string[] = [];
    safe.forEach((t) => {
      const bullet = `• ${t || "-"}`;
      const wrapped = doc.splitTextToSize(bullet, maxWidth) as string[];
      lines.push(...wrapped);
      lines.push("");
    });
    if (!lines.length) return ["-"];
    while (lines.length && lines[lines.length - 1] === "") lines.pop();
    return lines;
  };

  recordings.forEach((recording, index) => {
    if (index > 0) {
      doc.addPage();
    }
    const headerBottomY = drawTitleBlock();

    const party = getDisplayParty(recording);
    const { date, time } = formatDateTimeRange(recording?.selectedTimeSlot);

    const topY = headerBottomY + 16;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...TEXT_DARK);
    doc.text(`Meeting ${index + 1}`, marginX, topY);

    const cardX = marginX;
    const cardY = topY + 10;
    const cardW = pageWidth - marginX * 2;

    const detailX = cardX + 14;
    const detailW = cardW - 28;

    const labelFs = 7;
    const valueFs = 7;
    const labelLineH = 9;
    const valueLineH = 9;

    let y = cardY + 18;

    const labelColor: [number, number, number] = [90, 96, 104];
    const valueColor: [number, number, number] = [55, 60, 67];

    const colGap = 10;
    const colW = (detailW - 2 * colGap) / 3;
    const col1X = detailX;
    const col2X = detailX + colW + colGap;
    const col3X = detailX + (colW + colGap) * 2;

    const nameTitle = [fullName(party), party?.title].filter(Boolean).join(", ");
    const dateTimeMerged = `${date} · ${time}`;

    const nameTitleLines = doc.splitTextToSize(nameTitle || "-", colW) as string[];
    const companyLines = doc.splitTextToSize(party?.company || "-", colW) as string[];
    const dateTimeLines = doc.splitTextToSize(dateTimeMerged, colW) as string[];

    doc.setFont("helvetica", "bold");
    doc.setFontSize(labelFs);
    doc.setTextColor(...labelColor);
    doc.text("Name", col1X, y);
    doc.text("Company", col2X, y);
    doc.text("Meeting Date", col3X, y);
    y += labelLineH + 3;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(valueFs);
    doc.setTextColor(...valueColor);

    doc.text(nameTitleLines, col1X, y);
    doc.text(companyLines, col2X, y);
    doc.text(dateTimeLines, col3X, y);

    const blocksH = Math.max(
      nameTitleLines.length,
      companyLines.length,
      dateTimeLines.length
    );
    const valuesH = Math.max(blocksH, 1) * valueLineH;
    y += valuesH + 6;

    const detailsHeight = Math.max(48, y - cardY + 6);

    const summaryY = cardY + detailsHeight + 12;
    const { recap, next } = getSummaryBuckets(recording);

    const contentMaxWidth = Math.max(80, cardW / 2 - 12);
    const recapLines = asBullets(recap, contentMaxWidth);
    const nextLines = asBullets(next, contentMaxWidth);

    autoTable(doc, {
      startY: summaryY,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: { top: 5, right: 6, bottom: 6, left: 6 },
        textColor: 35,
        overflow: "linebreak",
        valign: "top",
        lineWidth: 0.5,
        lineColor: TABLE_BORDER,
      },
      head: [["MEETING RECAP", "NEXT STEPS"]],
      body: [[recapLines.join("\n"), nextLines.join("\n")]],
      tableWidth: cardW,
      margin: { left: marginX, right: marginX },
      headStyles: {
        fontStyle: "bold",
        fontSize: 8,
        textColor: 255,
        fillColor: NAVY,
        halign: "center",
        lineWidth: 0.5,
        lineColor: TABLE_BORDER,
      },
      columnStyles: {
        0: { cellWidth: cardW / 2 },
        1: { cellWidth: cardW / 2 },
      },
    });
  });

  const pageCount = doc.getNumberOfPages();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 26, {
      align: "right",
    });
  }

  return doc.output("blob");
}
